import * as React from 'react';
import Box from '@mui/material/Box';

import isPointerInside from 'shared/helpers/isPointerInside';
import { useEvent, useHoverDirty } from 'react-use';
import mergeRefs from 'merge-refs';

const ResponsiveDrawer: React.FC<Props> = React.forwardRef(
    (props, ref): React.ReactElement => {
        const {
            Footer,
            children,
            onClose,
            onSave,
            onZoomChanged,
            onSubmit,
            onBack,
            onModalChanged,
            onFailValidation,
            innerModalDocumentReworkRef,
            innerModalCompletedRef,
            isDocumentSuccess,
            categoryMenuName,
            categoryDefaultValue,
            documentPlaceholder,
            innerRef,
            documentRef,
            isLoading,
            unexpectedType,
            updateGroup,
            isFullScreen,
            setFullScreen,
            canCategorize,
            isActiveReworks,
            mainContainerRef,
        } = props;
        const footerRef = React.useRef<Element>(null);
        const isHovering = useHoverDirty(footerRef);
        const innerContentWrapperRef = React.useRef<HTMLElement | null>(null);
        const [scrollBarHeight, setScrollBarHeight] = React.useState<number>(0);
        const [isZoomChanged, setZoomChanged] = React.useState<boolean>(false);

        useEvent('resize', () => {
            const { current: contentWrapper } = innerContentWrapperRef;
            if (contentWrapper) {
                const scrollbarHeight =
                    contentWrapper.offsetHeight - contentWrapper.clientHeight;
                setScrollBarHeight(scrollbarHeight);
            }
        });
        React.useLayoutEffect(() => {
            if (isZoomChanged) {
                const { current: contentWrapper } = innerContentWrapperRef;
                if (contentWrapper) {
                    const scrollbarHeight =
                        contentWrapper.offsetHeight -
                        contentWrapper.clientHeight;
                    setScrollBarHeight(scrollbarHeight);
                }

                setZoomChanged(false);
            }
        }, [isZoomChanged]);
        const handleZoomChanged = React.useCallback(
            (val: number) => {
                setZoomChanged(true);
                if (typeof onZoomChanged === 'function') {
                    onZoomChanged(val);
                }
            },
            [onZoomChanged]
        );

        React.useImperativeHandle(
            ref,
            () => ({
                /**
                 * Check pointer is on footer area
                 * @param {MouseEvent} event - event object
                 * @return {boolean} condition to check mouse position inside footer container.
                 */
                isPointerOnFooter: (event: MouseEvent) => {
                    const { current: element } = footerRef;
                    const { current: contentWrapper } = innerContentWrapperRef;
                    if (contentWrapper) {
                        if (
                            event.y > contentWrapper.clientHeight &&
                            event.y < contentWrapper.offsetHeight
                        ) {
                            return true;
                        }
                    }
                    if (element) return isPointerInside(element, event);
                    return isHovering;
                },
            }),
            [isHovering]
        );
        const footerContainerSX = React.useMemo(() => {
            if (typeof isFullScreen === 'boolean') {
                return {
                    bottom: `${scrollBarHeight}px !important`,
                    width: isFullScreen ? '270px' : 'auto    ',
                    alignSelf: isFullScreen ? 'end' : 'initial',
                };
            }
            return {
                bottom: `${scrollBarHeight}px !important`,
            };
        }, [isFullScreen, scrollBarHeight]);

        const mainContainerSX = React.useMemo(() => {
            if (typeof isFullScreen === 'boolean') {
                return {
                    backgroundColor: isFullScreen
                        ? '#F6F6F6 !important'
                        : 'transparent',
                };
            }
            return {
                backgroundColor: '#F6F6F6 !important',
            };
        }, [isFullScreen]);
        return (
            <Box className="footer-wrapper fixpacket-container">
                <Box
                    ref={mergeRefs(mainContainerRef, innerContentWrapperRef)}
                    component="main"
                    aria-label="container with footing menu"
                    className="main-content"
                    sx={mainContainerSX}
                >
                    {children}
                </Box>
                <Box
                    component="nav"
                    className="footer-container"
                    ref={footerRef}
                    sx={footerContainerSX}
                >
                    <Footer
                        ref={innerRef}
                        canCategorize={
                            typeof canCategorize === 'boolean'
                                ? canCategorize
                                : true
                        }
                        unexpectedType={unexpectedType}
                        isLoading={isLoading}
                        onModalChanged={onModalChanged}
                        onClose={onClose}
                        onSave={onSave}
                        onBack={onBack}
                        onSubmit={onSubmit}
                        onZoomChanged={handleZoomChanged}
                        onFailValidation={onFailValidation}
                        documentRef={documentRef}
                        isFullScreen={isFullScreen}
                        documentPlaceholder={documentPlaceholder ?? ''}
                        isDocumentSuccess={isDocumentSuccess}
                        categoryDefaultValue={categoryDefaultValue ?? ''}
                        categoryMenuName={categoryMenuName ?? ''}
                        innerModalDocumentReworkRef={
                            innerModalDocumentReworkRef
                        }
                        updateGroup={updateGroup}
                        innerModalCompletedRef={innerModalCompletedRef}
                        setFullScreen={setFullScreen}
                        isActiveReworks={isActiveReworks}
                    />
                </Box>
            </Box>
        );
    }
);

export default ResponsiveDrawer;
